<template>
  <!-- eslint-disable max-len -->
  <div>
    <main class="u-container">
      <section class="pb-6">
        <info-panel
          id="ivs-locked-out-banner"
          :display-panel="mapMfaErrors('VerificationLockedException', 'PasscodeLockedException', 'SmfaLockedException')"
          panel-type="info"
          info-type="info"
        >
          <strong>There have been too many verification attempts. If you would like
            to proceed, please select and follow one of the options below:</strong>
          <ul class="mobile">
            <li>Complete your change of address at a later time: {{ this.$store.get('mfa/error').message }}.</li>
          </ul>
          <p>OR</p>
          <ul class="mobile">
            <li>
              Continue entering your information here. Once complete, visit your
              local Post Office to validate your identity and activate your request.
            </li>
          </ul>
        </info-panel>
        <info-panel
          id="ivs-system-error-banner"
          :display-panel="mapMfaErrors('SystemErrorException', 'UnverifiedPhoneException', 'IvsValidationException')"
          panel-type="info"
          info-type="info"
        >
          <strong>We are experiencing a system error. If you would like to proceed,
            please select and follow one of the options below:</strong>
          <ul class="mobile">
            <li>Complete your online change of address at a later time.</li>
          </ul>
          <p>OR</p>
          <ul class="mobile">
            <li>
              Continue entering your information here. Once complete, visit
              your local Post Office to validate your identity and activate your request.
            </li>
          </ul>
        </info-panel>
        <info-panel
          id="ivs-timeout-banner"
          :display-panel="mfaError('SmfaTimeoutException')"
          panel-type="info"
          info-type="info"
        >
          <strong>The transaction has timed out. If you would like to proceed,
            please select and follow one of the options below:</strong>
          <ul class="mobile">
            <li>Complete your online change of address at a later time.</li>
          </ul>
          <p>OR</p>
          <ul class="mobile">
            <li>
              Continue entering your information here. Once complete, visit
              your local Post Office to validate your identity and activate your request.
            </li>
          </ul>
        </info-panel>
        <h1 class="font-bold text-4xl">
          Official USPS<sup>®</sup> Change-of-Address
        </h1>
        <ProgressBar
          v-if="!showNonControlExperience"
          :steps="6"
          :current="4"
        />
        <IndividualProgressBarWithSteps
          v-if="showNonControlExperience"
          :mobile="true"
        />
        <h2
          ref="progressHeading"
          class="text-xl"
          :class="{'mt-6': !showNonControlExperience}"
        >
          <template v-if="!showNonControlExperience">
            Step 4:&nbsp;
          </template>
          <span class="font-bold">Verify identity</span>
        </h2>
        <!-- Banner Messages -->
        <p
          v-if="movingFromPoBox"
          ref="bannerOldPoBox"
          class="mt-3 text-sm"
        >
          <strong>You are changing your address from a Post Office Box.</strong> Our address database has recognized your old address as a post office box.
          <template v-if="moverType === 'BUSINESS'">
            Please note that the USPS policy only allows the boxholder customer or authorized representative of the organization listed on <i>PS Form 1093, Application for Post Office Box Service</i> to file Change-of-Address (COA) orders. Forwarding of mail for other persons receiving mail at the box is the responsibility of the organization.
          </template>
          <template v-else>
            Please note that the USPS policy only allows the box customer who signs <i>PS Form 1093, Application for Post Office Box Service</i> to file Change-of-Address orders. Forwarding of mail for other persons receiving mail at the box is the responsibility of the official boxholder.
          </template>
        </p>
        <p
          v-if="newAddressDpo"
          ref="bannerNewDpo"
          class="mt-3 text-sm"
        >
          <strong>You are changing your address to a Diplomatic Post Office address.</strong> Our address database has recognized your new address as a Diplomatic Post Office address, and we will forward your mail as requested. Please note that the USPS policy does not support forwarding mail out of a Diplomatic Post Office addresses. This means if you change your address again in the future, you must contact your Diplomatic Post Office mail clerk to forward your mail.
        </p>
        <p
          v-if="newAddressCmra"
          ref="bannerNewCmra"
          class="mt-3 text-sm"
        >
          <strong>You are changing your address to a Private Mail Box.</strong> Our address database has recognized your new address as a Private Mail Box held at a Commercial Mail Receiving Agency (CMRA), and we will forward your mail as requested. Please note that the USPS policy does not support forwarding mail out of CMRAs. This means if you change your address again in the future, you must contact your Commercial Mail Receiving Agency to forward your mail.
        </p>
        <p
          v-if="newAddressInUniqueZip"
          ref="bannerNewUniqueZip"
          class="mt-3 text-sm"
        >
          <strong>Our address database has recognized your new address as a unique address</strong>, and we will forward your mail as requested. Please note that the USPS policy does not support forwarding mail from a unique address; if you change your address again in the future, you must contact the mail center for this business or institution to forward your mail.
        </p>
        <p
          v-if="newAddressBusiness && moverType !== 'BUSINESS'"
          ref="bannerResToBiz"
          class="mt-3 text-sm"
        >
          <strong>You are changing your address from a residential address to a business address.</strong> Our address database has recognized your new address as a business or organization (e.g., corporations, universities, hospitals, hotels), and we will forward your mail as requested. Please note that the USPS policy does not allow individual or family movers to forward mail from a business/organization address. This means if you change your address again in the future, the business/organization will be responsible for forwarding your mail.
        </p>
        <p
          ref="subheading"
          class="mt-4"
        >
          For your security, we need to verify your identity using a valid credit or debit card. You will be charged {{ icoaAmountFormatted }}. We accept the following cards:
        </p>
        <div
          class="flex mt-4"
        >
          <img
            :src="$options.AmericanExpressSVG"
            alt="American Express"
            height="32px"
            width="45px"
          >
          <img
            :src="$options.DiscoverSVG"
            alt="Discover"
            class="ml-1"
            height="32px"
            width="45px"
          >
          <img
            :src="$options.VisaSVG"
            alt="VISA"
            class="ml-1"
            height="32px"
            width="45px"
          >
          <img
            :src="$options.MastercardSVG"
            alt="Mastercard"
            class="ml-1"
            height="32px"
            width="45px"
          >
        </div>
        <template v-if="businessToMilitary">
          <p class="mt-4">
            <strong>Billing address</strong>
          </p>
          <p class="mt-2">
            To verify your identity, the address on file with your credit card company must match your old address.
          </p>
          <p
            ref="bizToMil"
            class="mt-6"
          >
            {{ hrMcoaAddress.line1 }}<br>
            {{ hrMcoaAddress.line2 }}
          </p>
          <div class="c-hairline mt-6" />
          <p class="mt-4">
            We'll ask for the rest of your credit or debit card information on the next page.
          </p>
        </template>
        <template v-else>
          <Tooltip
            class="mt-4"
          >
            <p>
              <strong>Select a billing address:</strong>
            </p>
            <template #tip>
              <p ref="tooltipCopy1">
                To verify your identity and prevent fraudulent Change-of-Address requests, we process a {{ icoaAmountFormatted }} fee to your credit or debit card. Your card's billing address should match either the old or new address you entered earlier during this process.
              </p>
              <p class="mt-3">
                Please note that the Internet Change-of-Address Service uses a high level of security on a secure server.
              </p>
            </template>
            <template slot="accessible">
              Billing details
            </template>
          </Tooltip>
          <div class="mt-4 text-center">
            <fieldset>
              <InputRadio
                ref="oldBillingType"
                v-model="billingAddressType"
                form-value="OLD"
                name="billing-type"
                class="c-input-radio-button--alt mt-1"
                button
                :class="{uppercase: true}"
                @input="analytics.click.oldBillingType"
              >
                <template>
                  {{ hrOldAddress }}
                </template>
              </InputRadio>
              <InputRadio
                ref="newBillingType"
                v-model="billingAddressType"
                form-value="NEW"
                name="billing-type"
                class="c-input-radio-button--alt mt-1"
                button
                :class="{uppercase: true}"
                @input="analytics.click.newBillingType"
              >
                <template>
                  {{ hrNewAddress }}
                </template>
              </InputRadio>
              <InputRadio
                v-if="billingAddressComplete && showThirdAddressOption"
                ref="otherBillingType"
                v-model="billingAddressType"
                form-value="OTHER"
                name="billing-type"
                class="c-input-radio-button--alt mt-1"
                button
                :class="{uppercase: true}"
                @input="analytics.click.otherBillingType"
              >
                <template>
                  {{ hrBillingAddress }}
                </template>
              </InputRadio>
            </fieldset>
            <template v-if="showThirdAddressOption">
              <p
                v-if="billingAddressType !== 'OTHER' && !billingAddressComplete"
                class="mt-2"
              >
                <!-- or -->
              </p>
              <button
                ref="otherAddressBtn"
                :class="{ 'mt-2': billingAddressComplete }"
                type="button"
                class="c-link underline"
                @click="showThirdAddressFields(); analytics.click.otherBillingType();"
              >
                <template v-if="billingAddressType === 'OTHER'">
                  Edit Billing Address
                </template>
                <template v-if="billingAddressType !== 'OTHER' && !billingAddressComplete">
                  Enter a Different Address
                </template>
              </button>
              <p
                v-if="billingAddressType === 'OTHER' && informedDelivery"
                ref="informedDeliveryCopy"
                :class="{ 'mt-6': !billingAddressComplete }"
              >
                For security reasons Informed Delivery is currently not available for customers who are registering in conjunction with a third party address. Please visit
                <a
                  href="https://informeddelivery.usps.com/box/pages/intro/start.action"
                  rel="noopener noreferrer"
                  target="_blank"
                  class="c-link c-link--inline"
                >informeddelivery.usps.com</a> for alternative registration options.
              </p>
            </template>

            <div class="flex mt-4">
              <img
                :src="$options.AmericanExpressSVG"
                alt="American Express"
                height="32px"
                width="45px"
              >
              <img
                :src="$options.MastercardSVG"
                alt="Mastercard"
                class="ml-1"
                height="32px"
                width="45px"
              >
              <img
                :src="$options.VisaSVG"
                alt="VISA"
                class="ml-1"
                height="32px"
                width="45px"
              >
              <img
                :src="$options.DiscoverSVG"
                alt="Discover"
                class="ml-1"
                height="32px"
                width="45px"
              >
            </div>
            <p
              :class="{ 'mt-6': billingAddressType === 'OTHER' || !billingAddressComplete }"
            >
              We'll collect the rest of your payment information on the next step.
            </p>
          </div>
        </template>
      </section>
      <transition
        appear
        name="fade"
      >
        <section
          v-if="flagEMFEnabled && emf"
          ref="emf"
        >
          <ExtensionSummary
            ref="emfSummary"
            :extension-cost="emfProperties.optionCost"
            :extension-period="emfProperties.optionPeriod"
            :icoa-amount="icoaAmount || 105"
            show
            class="mt-4 mb-6"
            @clear-selection="emf = ''"
          />
        </section>
      </transition>
      <transition
        name="fade"
        appear
      >
        <section
          v-if="renderV3Challenge"
          class="mt-6"
        >
          <p class="mb-4">
            For your security, clicking the box below confirms that this Change-of-Address form is being completed by a person, and not an automated query.
          </p>
          <vue-recaptcha
            id="recaptcha-challenge"
            ref="recaptchaChallenge"
            :sitekey="getRecaptchaV2Key()"
            @click.native="analytics.click.recaptchaVerified"
            @verify="recaptchaChallengeToken=$event;"
            @expired="recaptchaChallengeToken='';"
          />
          <transition
            appear
            name="fade"
          >
            <p
              v-if="errors.recaptchaChallengeToken && errors.recaptchaChallengeToken.hasError"
              class="c-input-text__helper c-input-helper errors"
            >
              {{ errors.recaptchaChallengeToken.message }}
            </p>
          </transition>
        </section>
      </transition>
      <StickyPanel>
        <template slot="buttons">
          <router-link
            :to="{ name: 'verify-info-read', query: $route.query }"
            class="c-btn--mobile c-btn--secondary--mobile simplify__nav-button"
            replace
            @click.native="analytics.click.back()"
          >
            Back
          </router-link>
          <button
            id="next"
            ref="submit"
            class="c-btn--mobile simplify__nav-button"
            @click="captureNavigation()"
          >
            Next
          </button>
        </template>
      </StickyPanel>
    </main>
    <!-- 3rd Address Field Base Control Modal -->
    <Modal
      ref="modal"
      v-model="isModalOpen"
      @user-dismissed="billingAddressType = 'OLD'; resetBillingAddress();"
    >
      <template v-if="billingAddressType === 'OTHER'">
        <form
          v-if="currentModalStep === 0"
          ref="modalAddressForm"
          class="my-5"
          novalidate
          @submit.prevent="billingAddressError = ''; validate();"
        >
          <h4 class="font-bold mt-5">
            Enter Different Address
          </h4>
          <Tooltip class="mt-2">
            <template #tip>
              <p>
                This is the billing address for your credit or debit card. Enter a valid address as you know it, and remember to include apartment/suite number if applicable.
              </p>
              <p class="mt-3">
                If your apartment/suite number needs further clarification, try adding or removing a dash (for example, change 1-B to 1B or 1 B).
              </p>
              <p class="mt-3">
                If your address is a PO Box, enter it in the Street Address field. Please note that Change-of-Address requests from non-USPS PO Boxes cannot be processed.
              </p>
            </template>
            <template slot="accessible">
              Billing address description
            </template>
          </Tooltip>
          <InputListBox
            ref="billingStreetAddress"
            v-model="billingStreetAddress"
            label="Street Address"
            placeholder="Street Address"
            name="billing-street-address"
            clear
            :has-error="errors.billingStreetAddress.hasError"
            :async-fn="streetSearch({
              cityName: billingCity,
              state: billingState,
              zip: billingZipCode,
            })"
            :transform-compare="streetCompare"
            :transform-label="streetLabel"
            :transform-value="streetValue"
            @input="billingPoBoxNumber = ''; billingPrivateMailbox = ''; billingAddressError = ''; clearErrors('billingstreetAddress')"
            @select="billingCity = $event.cityName;
                     billingState = $event.state;
                     billingZipCode = $event.zip;"
            @clear="clearContent"
          >
            {{ errors.billingStreetAddress.message }}
          </InputListBox>
          <InputText
            v-if="billingState === 'PR'"
            v-model="billingUrbanizationName"
            label="Urbanization Name (Puerto Rico only)"
            name="billing-urbanization-name"
            placeholder="URB HIGHLAND GDNS"
            class="mt-3"
            clear
            :has-error="errors.billingUrbanizationName.hasError"
            @clear="clearContent"
          >
            <template #helper>
              {{ errors.billingUrbanizationName.message }}
            </template>
          </InputText>
          <InputText
            v-if="showBillingPoBox"
            ref="billingPoBox"
            v-model="billingPoBoxNumber"
            name="billing-po-box-number"
            placeholder="PO Box 123"
            class="mt-3"
            clear
            :has-error="errors.billingPoBoxNumber.hasError"
            @clear="clearContent"
            @input="billingAddressError = '';"
          >
            <Tooltip slot="label">
              <span>Po Box</span>
              <template slot="tip">
                <p>
                  <span class="font-bold">Note:</span> The address you provided is not considered a proper mailing address within this ZIP Code™. A proper mailing address within this ZIP Code™ requires a PO Box address. Please input your PO Box number in the “PO Box” field and click "Next" to continue.<br><span class="font-bold">A PO Box is required to proceed.</span>
                </p>
              </template>
              <template slot="accessible">
                Billing address PO box note
              </template>
            </Tooltip>
            <template #helper>
              {{ errors.billingPoBoxNumber.message }}
            </template>
          </InputText>
          <InputText
            v-if="showBillingPrivateMailbox"
            v-model="billingPrivateMailbox"
            label="Private Mailbox"
            name="billing-private-mailbox"
            placeholder="PMB 123"
            clear
            :has-error="errors.billingPrivateMailbox.hasError"
            @clear="clearContent"
          >
            <template #helper>
              {{ errors.billingPrivateMailbox.message }}
            </template>
          </InputText>
          <InputListBox
            ref="billingCity"
            v-model="billingCity"
            label="City"
            placeholder="City"
            name="billing-city"
            class="mt-3"
            clear
            :has-error="errors.billingCity.hasError"
            :async-fn="citySearch({
              state: billingState,
              zip: billingZipCode,
            })"
            :transform-compare="zipCompare"
            :transform-label="cityLabel"
            :transform-value="cityValue"
            @select="billingState = $event.state; billingZipCode = $event.zip;"
            @clear="clearContent"
            @input="billingAddressError = ''; clearErrors('billingcity')"
          >
            {{ errors.billingCity.message }}
          </InputListBox>
          <InputState
            ref="billingState"
            v-model="billingState"
            label="State"
            name="billing-state"
            class="mt-3"
            :has-error="errors.billingState.hasError"
          >
            <template #helper>
              {{ errors.billingState.message }}
            </template>
          </InputState>
          <InputZipCode
            ref="billingZipCode"
            v-model="billingZipCode"
            label="ZIP Code™"
            placeholder="00000"
            name="billing-zip-code"
            class="mt-3"
            clear
            :has-error="errors.billingZipCode.hasError"
            :async-fn="zipSearch({
              cityName: billingCity,
              state: billingState,
            })"
            :transform-compare="zipCompare"
            :transform-label="zipLabel"
            :transform-value="zipValue"
            @select="billingCity = $event.cityName; billingState = $event.state;"
            @clear="clearContent"
          >
            <template slot="helper">
              {{ errors.billingZipCode.message }}
            </template>
          </InputZipCode>
          <button
            class="c-btn w-full mt-5"
            @click="analytics.otherBillingModal.next"
          >
            Next
          </button>
        </form>
        <form
          v-if="currentModalStep === 1"
          ref="preparerTypeForm"
          class="my-5"
          novalidate
          @submit.prevent="modalNextStep"
        >
          <h4 class="mt-5 text-center font-bold">
            Because you entered a different billing address, we need some additional information for verification purposes.
          </h4>
          <p class="mt-5 text-center font-bold">
            Are you changing your own address or helping someone else?
          </p>
          <fieldset>
            <InputRadio
              ref="ownRadio"
              v-model="preparerType"
              form-value="MOVER"
              name="change-address-type"
              button
              class="c-input-radio-button--alt mt-4"
              @input="analytics.otherBillingModal.mover"
            >
              I'm changing my own address
            </InputRadio>
            <InputRadio
              ref="preparerRadio"
              v-model="preparerType"
              form-value="PREPARER"
              name="change-address-type"
              button
              class="c-input-radio-button--alt mt-1"
              @input="analytics.otherBillingModal.preparer"
            >
              I'm helping someone else
            </InputRadio>
          </fieldset>
          <div
            v-if="bufferStartDate || sdBuffered || sdBufferedAlwaysToggle"
            class="mt-4 text-center"
          >
            <p class="text-md">
              <strong>We had to change your forwarding start date</strong>
            </p>
            <p
              id="buffer-start-date-msg"
              ref="bufferMessage"
              class="mt-3 text-sm"
            >
              When using a billing address other than your new or old address to validate your identity, the forwarding date must be no sooner than {{ moverType === 'BUSINESS' ? '7 days' : '7 days' }} from today. We've modified your start date to the earliest date possible, but you can make it even later if you prefer.
            </p>
            <InputDatePicker
              ref="bufferDatepicker"
              v-model="bufferedStartDate"
              :min="minBufferedStartDate"
              :max="maxStartDate"
              :open-date="minBufferedStartDate"
              name="buffered-start-date"
              inline
              class="mt-3"
              :has-error="errors.bufferedStartDate.hasError"
            >
              <template #helper>
                {{ errors.bufferedStartDate.message }}
              </template>
            </InputDatePicker>
            <p class="mt-3 font-bold">
              Mail Forwarding Date
            </p>
            <p>{{ getFormattedDate(bufferedStartDate, 'iiii, MMMM d, yyyy') }}</p>
            <p class="mt-3 text-sm">
              {{ getFormattedDate(minBufferedStartDate, 'MM/dd/yyyy') }} is the soonest we can start forwarding. If you choose a later start date, your end date will also be pushed back.
            </p>
            <p
              ref="holdMailNote"
              class="mt-3 text-sm"
            >
              <span class="font-bold">
                Note:
              </span>
              If you are moving within {{ moverType === 'BUSINESS' ? '30 days' : '7 days' }}, you can have your mail held at your post office for 3 to 30 days by <a
                href="https://holdmail.usps.com/holdmail/"
                target="_blank"
                class="c-link c-link--inline"
              >putting your mail on hold online</a> or visiting your local post office.
            </p>
          </div>
          <button
            class="c-btn w-full mt-4"
            @click="preparerType === 'MOVER'
              ? analytics.otherBillingModal.continue()
              : analytics.otherBillingModal.next()
            "
          >
            Continue
          </button>
        </form>
        <!-- 3rd Address Preparer Contact Information -->
        <form
          v-if="currentModalStep === 2"
          ref="preparerFields"
          class="my-5"
          novalidate
          @submit.prevent="validate"
        >
          <h4 class="font-bold mt-5">
            Please enter YOUR name and contact information.
          </h4>
          <p class="mt-5">
            We need this information in case we need to reach you with any questions about this Change-of-Address request. (Do NOT enter the mover's information below.)
          </p>
          <InputText
            ref="preparerFirstName"
            v-model="preparerFirstName"
            label="First Name"
            name="first-name"
            placeholder="First Name"
            class="mt-5"
            :has-error="errors.preparerFirstName.hasError"
            @input="errors.preparerFirstName.hasError = false"
          >
            <template
              v-if="errors.preparerFirstName.hasError"
              #helper
            >
              {{ errors.preparerFirstName.message }}
            </template>
          </InputText>
          <InputText
            ref="preparerMiddleName"
            v-model="preparerMiddleName"
            label="Middle Name or Initial"
            name="middle-name"
            placeholder="Middle Name or Initial"
            class="mt-5"
            :has-error="errors.preparerMiddleName && errors.preparerMiddleName.hasError"
            @input="handlePreparerMiddleName"
          >
            <template
              v-if="errors.preparerMiddleName && errors.preparerMiddleName.hasError"
              #helper
            >
              {{ errors.preparerMiddleName.message }}
            </template>
          </InputText>
          <InputText
            ref="preparerLastName"
            v-model="preparerLastName"
            label="Last Name"
            name="last-name"
            placeholder="Last Name"
            class="mt-5"
            :has-error="errors.preparerLastName.hasError"
            @input="errors.preparerLastName.hasError = false"
          >
            <template
              v-if="errors.preparerLastName.hasError"
              #helper
            >
              {{ errors.preparerLastName.message }}
            </template>
          </InputText>
          <InputText
            ref="preparerEmail"
            v-model.trim="preparerEmail"
            label="Email Address"
            name="email"
            placeholder="Hello@mail.com"
            class="mt-5"
            :has-error="errors.preparerEmail.hasError"
            @input="errors.preparerEmail.hasError = false"
          >
            <template
              v-if="errors.preparerEmail.hasError"
              #helper
            >
              {{ errors.preparerEmail.message }}
            </template>
          </InputText>
          <InputPhone
            ref="preparerPhoneNumber"
            v-model="preparerPhoneNumber"
            label="Phone Number"
            placeholder="(000) 000-0000"
            name="phone"
            class="mt-3"
            :has-error="errors.preparerPhoneNumber.hasError"
          >
            <template #helper>
              {{ errors.preparerPhoneNumber.message }}
            </template>
          </InputPhone>
          <InputDropdown
            ref="preparerPhoneType"
            v-model="preparerPhoneType"
            label="Phone Type"
            name="phone-type"
            class="mt-5"
            :has-error="errors.preparerPhoneType.hasError"
          >
            <option value="MOBILE">
              Mobile
            </option>
            <option value="HOME">
              Home
            </option>
            <template #helper>
              {{ errors.preparerPhoneType.message }}
            </template>
          </InputDropdown>
          <button
            class="c-btn w-full mt-5"
            @click="analytics.otherBillingModal.continue"
          >
            Continue
          </button>
        </form>
      </template>
    </Modal>
    <!-- for mfa modal-->
    <TwoFactor
      v-if="showMfaModal"
      :phone-number="mobilePhone"
      @close="showMfaModal = false"
    />
  </div>
</template>

<script>
import VueRecaptcha from 'vue-recaptcha';
import {
  InputDatePicker,
  InputDropdown,
  InputListBox,
  InputPhone,
  InputRadio,
  InputState,
  InputText,
  InputZipCode,
  Modal,
  ProgressBar,
  IndividualProgressBarWithSteps,
  Tooltip,
} from '@mgo/ui/lib/components';
import { mapErrors } from '@mgo/core/src/validations/utils';
import {
  addDays, parseISO, startOfDay,
} from 'date-fns';
import { track } from '../../analytics/events';
import { mobileAnalytics } from './analytics';
import ExtensionSummary from '../../components/ExtensionSummary.vue';
import addressViolations from '../../mixins/addressViolations';
import datePicker from '../../mixins/datePicker';
import identityCheckMixin from './mixin';
import InfoPanel from '../../components/info-panel.vue';
import grecaptcha from '../../mixins/grecaptcha';
import rapidKey from '../../mixins/rapidKey';

// SVG imports
import AmericanExpressSVG from '../../assets/images/_logo-american-express.svg';
import DiscoverSVG from '../../assets/images/_logo-discover.svg';
import MastercardSVG from '../../assets/images/_logo-mastercard.svg';
import VisaSVG from '../../assets/images/_logo-visa.svg';
import TwoFactor from '../TwoFactor/index.vue';
import StickyPanel from '../../components/StickyPanel.vue';

export default {
  name: 'MobileIdentityCheck',
  components: {
    ExtensionSummary,
    InputDatePicker,
    InputDropdown,
    InputListBox,
    InputPhone,
    InputRadio,
    InputState,
    InputText,
    InputZipCode,
    StickyPanel,
    Modal,
    ProgressBar,
    IndividualProgressBarWithSteps,
    Tooltip,
    InfoPanel,
    VueRecaptcha,
    TwoFactor,
  },
  mixins: [
    addressViolations,
    datePicker,
    grecaptcha,
    identityCheckMixin,
    rapidKey,
  ],
  data() {
    return {
      emf: 0,
      currentModalStep: 0,
      isModalOpen: false,
      recaptchaToken: 'STATIC', // used to pass validation
      showMfaModal: false,
    };
  },
  computed: {
    current() {
      return !this.showNonControlExperience ? 4 : 5;
    },
    totalSteps() {
      return !this.showNonControlExperience ? 7 : 8;
    },
    progressStep() {
      return (this.current / this.totalSteps);
    },
    sdBufferedAlwaysToggle() {
      return JSON.parse(sessionStorage.getItem('sdBufferedAlwaysToggle'));
    },
    visitedGlobalPayment() {
      return JSON.parse(sessionStorage.getItem('visitedGlobalPayment')) || false;
    },
    analytics() {
      return mobileAnalytics();
    },
    errors() {
      const fieldsToValidate = [];

      if (this.billingAddressType === 'OTHER' && this.currentModalStep === 0) {
        const billingAddressFields = [
          'billingStreetAddress',
          'billingCity',
          'billingState',
          'billingZipCode',
        ];
        fieldsToValidate.push(...billingAddressFields);
      }

      if (this.showBillingPrivateMailbox && this.currentModalStep === 0) {
        fieldsToValidate.push('billingPrivateMailbox');
      }

      if (this.showBillingPoBox && this.currentModalStep === 0) {
        fieldsToValidate.push('billingPoBoxNumber');
      }

      if (this.billingState === 'PR') {
        fieldsToValidate.push('billingUrbanizationName');
      }

      if ((this.bufferStartDate || this.sdBuffered || this.sdBufferedAlwaysToggle)
        && this.currentModalStep === 1) {
        fieldsToValidate.push('bufferedStartDate');
      }

      if (this.preparerType === 'PREPARER' && this.currentModalStep === 2) {
        const preparerFields = [
          'preparerFirstName',
          'preparerLastName',
          'preparerEmail',
          'preparerPhoneNumber',
          'preparerPhoneType',
        ];
        fieldsToValidate.push(...preparerFields);
      }

      if (this.renderV3Challenge && !this.isModalOpen) {
        fieldsToValidate.push('recaptchaChallengeToken');
      }

      return { ...mapErrors(this, fieldsToValidate), ...this.improvedFieldErrors };
    },
  },
  watch: {
    isModalOpen(current) {
      if (current) {
        this.analytics.otherBillingModal.opened();
      } else {
        this.analytics.otherBillingModal.closed();
      }
    },
  },
  created() {
    setTimeout(() => {
      if (this.bufferedStartDate) {
        const startDate = startOfDay(new Date(this.startDate));
        const bufferedStartDate = startOfDay(new Date(this.bufferedStartDate));
        const thirtyDaysFuture = addDays(parseISO(bufferedStartDate), 30);

        if (startDate < thirtyDaysFuture) {
          this.bufferedStartDate = thirtyDaysFuture;
        }
      }
    }, 2000);
  },
  methods: {
    async captureNavigation() {
      // eslint-disable-next-line no-unused-expressions
      const visitedGlobalPayment = JSON.parse(sessionStorage.getItem('visitedGlobalPayment'));
      this.validate(); this.analytics.click.next();
      if (this.sdBuffered && visitedGlobalPayment) {
        sessionStorage.setItem('sdBufferedAlwaysToggle', 'true');
      }
      sessionStorage.setItem('visitedGlobalPayment', 'true');
    },
    clearContent(payload) {
      this.clearErrors(payload.toLowerCase());
      const toKebobCase = (str) => str.replace(/-./g, (x) => x.toUpperCase()[1]);
      const transformed = toKebobCase(payload);
      this[transformed] = '';
      switch (payload) {
        case toKebobCase('billing-street-address'):
          this[toKebobCase('billing-po-box-number')] = '';
          break;
        default:
          break;
      }
    },
    showThirdAddressFields() {
      this.billingAddressType = 'OTHER';
      this.currentModalStep = 0;
      this.isModalOpen = true;
    },
    async validate() {
      this.lazy = false;
      this.fieldErrors = [];
      this.improvedFieldErrors = {};
      await this.$nextTick();
      if (
        this.flagRecaptchaEnabled
        && !this.renderV3Challenge
        && !this.isModalOpen
      ) {
        const action = this.billingAddressType === 'OTHER' ? 'identity_check_third' : 'identity_check';
        const success = await this.recaptchaV3Validate(action);
        this.recaptchaV3Success = success;
      }
      if (
        this.flagRecaptchaEnabled
        && !this.recaptchaV3Success
        && !this.renderV3Challenge
        && !this.recaptchaChallengeToken
        && !this.isModalOpen
      ) {
        this.lazy = true;
        return this.recaptchaInvokeChallenge();
      }
      this.patchInformedDelivery();

      if (!this.formIsValid) return this.scrollToFirstError();
      if (this.preparerType === 'MOVER') await this.deletePreparer();
      // OLD or NEW address for billing, submit to payment
      if (this.billingAddressType !== 'OTHER') return this.submit();
      // OTHER, THIRD address used for billing
      if (this.currentModalStep === 0 && this.isModalOpen) return this.saveBillingAddress();
      if (this.preparerType === 'PREPARER' && this.isModalOpen) {
        await this.savePreparer();
        const errs = Object.values(this.errors).filter((err) => err.hasError === true);
        if (errs.length) return null;
      }

      // Form is valid, form steps completed, close modal if open, otherwise submit to payment
      return this.isModalOpen ? this.modalReset() : this.submit();
    },
    async saveBillingAddress() {
      const payload = {
        ...this.coa,
        billingAddressType: this.billingAddressType,
        informedDelivery: false,
        startDate: this.bufferStartDate ? this.bufferedStartDate : this.startDate,
        sdBuffered: this.bufferStartDate,
        multipleAddresses: this.multipleAddresses === 'YES' ?? false,
      };
      const { xhr } = this.coa.multipleAddresses
        ? await this.$coa.businessCoaUpdate(payload)
        : await this.$coa.coaUpdate(payload);
      this.attachFieldErrors({ xhr });
      if (xhr?.exception || this.fieldErrors?.length) {
        this.$store.commit('DISABLE_LOADER');
        await this.$nextTick();
        this.scrollToFirstError();
        return;
      }
      const billingAddressResponse = await this.$coa.addressSave({
        addressType: 'BILLING',
        streetAddress: this.billingStreetAddress,
        city: this.billingCity,
        state: this.billingState,
        zipCode: this.billingZipCode,
        urbanizationName: this.billingUrbanizationName || null,
        privateMailbox: this.billingPrivateMailbox || null,
        poBoxNumber: this.billingPoBoxNumber || null,
      });

      this.attachFieldErrors(billingAddressResponse, 'billing');
      this.billingAddressError = billingAddressResponse?.xhr?.exception || '';

      if (billingAddressResponse?.xhr?.exception === 'InvalidPrimaryPoBoxException' && !this.billingStreetHasPoBox) {
        this.errors.billingPoBoxNumber.hasError = this.errors.billingStreetAddress.hasError;
        this.errors.billingPoBoxNumber.message = this.errors.billingStreetAddress.message;
        this.errors.billingStreetAddress.hasError = false;
        this.errors.billingStreetAddress.message = '';
      }
      await this.$nextTick();

      // log the defaultExceptions for billingAddress
      if (this.billingAddressError) {
        this.defaultException(this.billingAddressError);
      }

      // If there are logged errors
      // prevent the next step
      if (this.billingAddressError || this.fieldErrors.length) {
        this.scrollToFirstError();
        return;
      }

      this.billingAddress = billingAddressResponse;
      this.modalNextStep();
    },
    async savePreparer() {
      let personResponse;
      const personData = {
        personType: 'PREPARER',
        firstName: this.preparerFirstName,
        middleName: this.preparerMiddleName,
        lastName: this.preparerLastName,
        email: this.preparerEmail,
        emailConfirm: this.preparerEmail,
        [`${this.preparerPhoneType.toLowerCase()}Phone`]: this.preparerPhoneNumber,
        phoneType: this.preparerPhoneType,
      };
      if (this.coa.multipleAddresses) {
        personResponse = await this.$coa.businessPersonSave(personData);
      } else {
        personResponse = await this.$coa.personSave(personData);
      }
      this.attachFieldErrors(personResponse);
      this.prepareFieldErrors(personResponse);

      track(this.preparerUUID ? 'PersonUpdated' : 'PersonCreated',
        {
          personType: 'PREPARER',
          phoneType: this.preparerPhoneType,
        });
    },
    async deletePreparer() {
      let preparerResponse;
      const personData = {
        personType: 'PREPARER',
        firstName: this.preparerFirstName,
        middleName: this.preparerMiddleName,
        lastName: this.preparerLastName,
        email: this.preparerEmail,
        emailConfirm: this.preparerEmail,
        [`${this.preparerPhoneType.toLowerCase()}Phone`]: this.preparerPhoneNumber,
        phoneType: this.preparerPhoneType,
      };

      if (this.coa.multipleAddresses) {
        preparerResponse = await this.$coa.businessPersonDelete(personData);
      } else {
        preparerResponse = await this.$coa.personDelete(personData);
      }
      this.attachFieldErrors(preparerResponse);
    },
    modalNextStep() {
      if (this.currentModalStep === 1 && this.preparerType === 'MOVER') {
        this.modalReset();
      }

      if (this.currentModalStep < 2) {
        this.lazy = true;
        this.currentModalStep += 1;
      }
    },
    // reset the billing address fields if modal is dismissed
    // before the billing address is saved
    // https://imagitas.atlassian.net/browse/MGOD-4223?focusedCommentId=63092
    resetBillingAddress() {
      if (Object.entries(this.billingAddress).length === 0) {
        this.billingStreetAddress = '';
        this.billingCity = '';
        this.billingState = '';
        this.billingZipCode = '';
        this.billingZip9 = '';
        this.billingUrbanizationName = '';
        this.billingPoBoxNumber = '';
        this.billingPrivateMailbox = '';
        this.billingAddressUUID = '';
      }
    },
    modalReset() {
      this.isModalOpen = false;
      this.currentModalStep = 0;
    },

    submitLogValidation() {
      return this.post(`${this.api}/mgo-api/api/v1/coa/${this.coaUUID}/log-validation`, {});
    },
    async submit() {
      if (this.$store.state.loading) {
        this.submitLogValidation();
        return;
      }

      if (!this.informedDeliveryEligible) {
        this.informedDelivery = false;
      }

      this.$store.commit('ENABLE_LOADER');

      const payload = {
        ...this.coa,
        billingAddressType: this.billingAddressType,
        informedDelivery: this.informedDelivery,
        startDate: this.bufferStartDate ? this.bufferedStartDate : this.startDate,
        sdBuffered: this.bufferStartDate,
        multipleAddresses: this.coa.multipleAddresses,
      };

      const { xhr } = this.coa.multipleAddresses
        ? await this.$coa.businessCoaUpdate(payload)
        : await this.$coa.coaUpdate(payload);

      this.attachFieldErrors({ xhr });

      if (xhr?.exception || this.fieldErrors?.length) {
        this.$store.commit('DISABLE_LOADER');
        await this.$nextTick();
        this.scrollToFirstError();
        return;
      }

      track('CoaUpdated',
        {
          dcoa: this.dcoa,
          forwardType: this.forwardType,
          informedDelivery: this.informedDelivery,
          moverType: this.moverType,
          startDate: this.startDate,
          stopDate: this.stopDate,
        });

      const billingPayload = {
        addressType: 'BILLING',
      };

      if (this.billingAddressType === 'OTHER') {
        Object.assign(billingPayload, {
          streetAddress: this.billingStreetAddress,
          city: this.billingCity,
          state: this.billingState,
          zipCode: this.billingZipCode,
          urbanizationName: this.billingUrbanizationName,
          privateMailbox: this.billingPrivateMailbox,
          poBoxNumber: this.billingPoBoxNumber,
        });
      } else {
        [
          'streetAddress',
          'city',
          'state',
          'zipCode',
          'urbanizationName',
          'privateMailbox',
          'poBoxNumber',
        ].forEach((field) => {
          billingPayload[field] = this[`${this.billingAddressType.toLowerCase()}Address`][field];
        });
      }

      const billingAddressResponse = this.coa.multipleAddresses
        ? await this.$coa.businessBillingAddressSave(this.$coa.orderId, billingPayload)
        : await this.$coa.addressSave(billingPayload);

      this.attachFieldErrors(billingAddressResponse);
      this.billingAddressError = billingAddressResponse?.xhr?.exception || '';
      if ((this.billingAddressError || this.fieldErrors.length) && this.billingAddressType === 'OTHER') {
        this.isModalOpen = true;
        this.validate();
      }
      await this.$nextTick();

      // log the defaultExceptions for billingAddress
      if (this.billingAddressError) {
        this.defaultException(this.billingAddressError);
      }
      // If there are logged errors
      // prevent the next step
      if (this.billingAddressError || this.fieldErrors.length) {
        if (this.billingAddressType === 'OTHER') {
          this.isModalOpen = true;
        }
        this.$store.commit('DISABLE_LOADER');

        this.scrollToFirstError();
        return;
      }

      if (this.flagEMFEnabled) {
        if (!this.showNonControlExperience && this.forwardType === 'PERMANENT') {
          const extensionList = await this.$coa.extensionGetAll(this.coa?.visitId);
          this.emf = extensionList?.selectedOption || 0;
        }

        const ext = this.extensionSelected || 0;
        await this.$coa.extensionSave(this.emf ? this.emf : ext,
        this.coa?.visitId);
      }

      // log the amsStatusCodes on 200 responses for billing addresses
      if (billingAddressResponse?.addressType
        && this.isAmsMatch(billingAddressResponse?.amsStatusCode)) {
        this.billingStreetAddress = billingAddressResponse?.streetAddress;
        this.billingCity = billingAddressResponse?.city;
        this.billingState = billingAddressResponse?.state;
        this.billingZipCode = billingAddressResponse?.zipCode;
      }

      this.next();
    },
    handlePreparerMiddleName() {
      if (this.errors.preparerMiddleName) {
        this.errors.preparerMiddleName.hasError = false;
      }
    },
  },
  AmericanExpressSVG,
  DiscoverSVG,
  MastercardSVG,
  VisaSVG,
};
</script>
